<template>
  <v-container id="pdi-user" fluid tag="section" class="dashboard-container" style="padding: 24px 96px;">
    <v-row class="mb-2 ma-n2" align="center">
      <v-col cols="12" sm="auto" class="pa-2">
        <div class="d-flex align-center">
          <v-icon color="#00E5FF" large class="mr-3">mdi-school</v-icon>
          <h1 class="text-h5 font-weight-bold text--primary">{{ $t('pdi.title') }}</h1>
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="12" sm="auto" class="pa-2">
        <v-btn v-if="!isViewOnlyMode" color="#00E5FF" height="40" depressed class="px-5 font-weight-bold action-btn black--text" style="border-radius: 8px;" @click="openCreateDialog">
          <v-icon left color="black">mdi-plus</v-icon> {{ $t('pdi.new') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mb-2 ma-n2 top-cards-row">
      <v-col cols="12" md="6" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 top-card flex-grow-1">
          <v-card-text class="pa-5 d-flex flex-column justify-center align-start" style="height: 100%">
            <v-icon large color="#00E5FF" class="mb-3">mdi-rocket-launch</v-icon>
            <div class="text-caption text--secondary mb-1">{{ $t('pdi.activePdis') }}</div>
            <div class="text-h3 font-weight-bold" style="color: #00E5FF;">{{ activePdisCount }}</div>
            <div class="text-caption text--secondary mt-1">{{ $t('pdi.activePdisDesc') }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 top-card flex-grow-1">
          <v-card-text class="pa-5 d-flex flex-column justify-center align-start" style="height: 100%">
            <v-icon large color="success" class="mb-3">mdi-target-account</v-icon>
            <div class="text-caption text--secondary mb-1">{{ $t('pdi.completedGoals') }}</div>
            <div class="text-h3 font-weight-bold success--text">{{ completedGoalsCount }}/{{ totalGoalsCount }}</div>
            <div class="text-caption text--secondary mt-1">{{ goalsCompletionPercentage }}{{ $t('pdi.totalOf') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row align="center" class="ma-n2 mb-4 mt-2">
      <v-col cols="auto" class="pa-2">
        <v-chip-group v-model="pdiFilter" mandatory>
          <v-chip value="all" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': pdiFilter === 'all'}">{{ $t('pdi.filter.all') }}</v-chip>
          <v-chip value="draft" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': pdiFilter === 'draft'}">{{ $t('pdi.filter.draft') }}</v-chip>
          <v-chip value="pending_approval" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': pdiFilter === 'pending_approval'}">{{ $t('pdi.filter.pendingApproval') }}</v-chip>
          <v-chip value="approved" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': pdiFilter === 'approved'}">{{ $t('pdi.filter.approved') }}</v-chip>
          <v-chip value="in_progress" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': pdiFilter === 'in_progress'}">{{ $t('pdi.filter.inProgress') }}</v-chip>
          <v-chip value="completed" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': pdiFilter === 'completed'}">{{ $t('pdi.filter.completed') }}</v-chip>
          <v-chip value="rejected" outlined class="theme-chip theme-chip-filter font-weight-bold" :class="{'active': pdiFilter === 'rejected'}">{{ $t('pdi.filter.rejected') }}</v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <v-row class="ma-n2">
      <v-col cols="12" class="pa-2">
        <v-card
          v-for="pdi in filteredPdis"
          :key="pdi.id"
          class="elevment-card elevation-0 mb-3 meeting-list-card"
        >
          <v-card-text class="pa-4">
            <v-row align="center" class="ma-0">
              
              <v-col cols="auto" class="pa-0 pr-4">
                <v-avatar size="48" :color="getStatusColor(pdi.status) + '20'" class="elevation-0">
                  <v-icon :color="getStatusColor(pdi.status)">{{ getStatusIcon(pdi.status) }}</v-icon>
                </v-avatar>
              </v-col>

              <v-col class="pa-0 pr-4">
                <div class="mb-1 d-flex align-center">
                  <div class="text-subtitle-1 font-weight-bold text--primary mr-3" style="line-height: 1.2;">
                    {{ pdi.title }}
                  </div>
                </div>

                <div class="d-flex flex-wrap gap-1 mt-1 align-center">
                  <v-chip x-small outlined class="mr-2 mb-1 elevation-0 theme-chip-topic">
                    <v-icon x-small left>mdi-tag</v-icon> {{ pdi.category || 'Sem categoria' }}
                  </v-chip>
                  <span class="text-caption text--secondary mb-1">
                    <v-icon x-small color="grey">mdi-calendar</v-icon>
                    {{ formatDate(pdi.deadline) }}
                  </span>
                </div>

                <div class="mt-3" style="max-width: 300px;">
                  <div class="d-flex justify-space-between align-center mb-1">
                    <span class="text-caption text--secondary">Progresso</span>
                    <span class="text-caption font-weight-bold" :class="getProgressColor(pdi.progress) + '--text'">{{ pdi.progress || 0 }}%</span>
                  </div>
                  <v-progress-linear
                    :value="pdi.progress || 0"
                    :color="getProgressColor(pdi.progress)"
                    height="6"
                    rounded
                    background-color="rgba(128,128,128,0.1)"
                  />
                </div>
              </v-col>

              <v-col cols="auto" class="text-right pa-0 pr-3" style="min-width: 140px;">
                <v-chip outlined x-small class="elevation-0 mb-2" :class="getStatusChipClass(pdi.status)">
                  {{ getStatusLabel(pdi.status) }}
                </v-chip>

                <div class="mt-1">
                  <div class="text-subtitle-2 font-weight-bold" :class="pdi.status === 'COMPLETED' ? 'success--text' : 'text--primary'">
                    <v-icon x-small :color="pdi.status === 'COMPLETED' ? 'success' : '#00E5FF'" class="mr-1">mdi-fire</v-icon>
                    +{{ pdi.xpReward || 500 }} XP
                  </div>
                </div>
              </v-col>

              <v-col cols="auto" class="pa-0">
                <v-btn icon small color="grey" class="expand-btn" @click="toggleExpand(pdi.id)">
                  <v-icon>{{ expandedPdis.includes(pdi.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-expand-transition>
            <div v-show="expandedPdis.includes(pdi.id)">
              <v-divider class="theme-divider" />
              <v-card-text class="pa-5 expanded-area" :style="{ backgroundColor: $vuetify.theme.dark ? '#121212' : '#F5F5F7' }">
                
                <div class="mb-4">
                  <div class="d-flex align-center mb-2">
                    <v-icon color="#00E5FF" small class="mr-2">mdi-note-text-outline</v-icon>
                    <span class="text-subtitle-2 font-weight-bold text--primary">Descrição</span>
                  </div>
                  <div class="text-body-2 pl-7 text--secondary" style="line-height: 1.5;">
                    {{ pdi.description || 'Sem descrição disponível.' }}
                  </div>
                </div>

                <div class="mb-4">
                  <div class="d-flex align-center mb-2">
                    <v-icon color="#00E5FF" small class="mr-2">mdi-target</v-icon>
                    <span class="text-subtitle-2 font-weight-bold text--primary">Metas ({{ pdi.goals ? pdi.goals.length : 0 }})</span>
                  </div>
                  
                  <div class="pl-7">
                    <v-list dense class="transparent-bg pa-0">
                      <v-list-item v-for="(goal, index) in pdi.goals" :key="index" class="px-0 min-h-auto">
                        <v-list-item-icon class="mr-3 my-1">
                          <v-icon small :color="(goal.status === 'DONE' || goal.completed) ? 'success' : 'grey'">
                            {{ (goal.status === 'DONE' || goal.completed) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content class="py-1">
                          <v-list-item-title class="text-body-2" :class="(goal.status === 'DONE' || goal.completed) ? 'text-decoration-line-through text--secondary' : 'text--primary'">
                            {{ goal.title }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </div>
                </div>

                <v-alert v-if="pdi.status === 'REJECTED' && pdi.rejectionReason" type="error" outlined dense class="mb-4 elevment-alert">
                  <div class="text-subtitle-2 font-weight-bold mb-1">Motivo da Rejeição:</div>
                  <div class="text-body-2">{{ pdi.rejectionReason }}</div>
                </v-alert>

                <div class="d-flex justify-end mt-4 gap-2 flex-wrap">
                  <v-btn v-if="pdi.status === 'DRAFT'" color="primary" outlined small class="font-weight-bold action-btn-outlined" :disabled="isViewOnlyMode" @click="submitForApproval(pdi)">
                    <v-icon small left>mdi-send</v-icon> Enviar para Aprovação
                  </v-btn>

                  <v-btn v-if="pdi.status === 'DRAFT' || pdi.status === 'PENDING_APPROVAL'" color="#00E5FF" outlined small class="font-weight-bold action-btn-outlined" :disabled="isViewOnlyMode" @click="editPdi(pdi)">
                    <v-icon small left>mdi-pencil</v-icon> Editar
                  </v-btn>

                  <v-btn v-if="pdi.status === 'DRAFT' || pdi.status === 'PENDING_APPROVAL'" color="error" outlined small class="font-weight-bold action-btn-outlined" :disabled="isViewOnlyMode" @click="deletePdi(pdi)">
                    <v-icon small left>mdi-delete</v-icon> Excluir
                  </v-btn>

                  <v-btn v-if="pdi.status === 'APPROVED'" color="info" outlined small class="font-weight-bold action-btn-outlined" :disabled="isViewOnlyMode" @click="startPdi(pdi)">
                    <v-icon small left>mdi-play-circle</v-icon> Iniciar PDI
                  </v-btn>

                  <v-btn v-if="pdi.status === 'IN_PROGRESS'" color="#00E5FF" depressed small class="font-weight-bold black--text" style="border-radius: 6px;" :disabled="isViewOnlyMode || (pdi.progress >= 100 && !canSelfApprove)" @click="openGoalsDialog(pdi)">
                    <v-icon small left color="black">mdi-check-circle</v-icon> Marcar Metas
                  </v-btn>

                  <v-btn v-if="pdi.status === 'IN_PROGRESS' && pdi.progress >= 100 && canSelfApprove" color="success" depressed small class="font-weight-bold white--text" style="border-radius: 6px;" :disabled="isViewOnlyMode" @click="completePdi(pdi)">
                    <v-icon small left color="white">mdi-check-all</v-icon> Concluir PDI
                  </v-btn>

                  <v-chip v-if="pdi.status === 'IN_PROGRESS' && pdi.progress >= 100 && !canSelfApprove" outlined x-small class="theme-badge success--text mt-1 ml-2">
                    <v-icon x-small left>mdi-clock-outline</v-icon> Aguardando avaliação do gestor
                  </v-chip>

                  <v-chip v-if="pdi.status === 'PENDING_APPROVAL'" outlined x-small class="theme-badge warning--text mt-1 ml-2">
                    <v-icon x-small left>mdi-clock-alert</v-icon> Aguardando aprovação
                  </v-chip>
                </div>
              </v-card-text>
            </div>
          </v-expand-transition>
        </v-card>

        <v-card v-if="pdis.length === 0 && !loading" class="pa-10 text-center elevment-card elevation-0">
          <v-icon size="64" color="rgba(128,128,128,0.2)">mdi-clipboard-text-outline</v-icon>
          <h3 class="text-h6 text--primary mt-5">Nenhum PDI cadastrado</h3>
          <p class="text-body-2 text--secondary mb-5">Comece criando seu primeiro Plano de Desenvolvimento Individual</p>
          <v-btn v-if="!isViewOnlyMode" color="#00E5FF" depressed @click="openCreateDialog" class="font-weight-bold black--text">
            <v-icon left color="black">mdi-plus</v-icon> Criar Primeiro PDI
          </v-btn>
        </v-card>

        <v-card v-if="loading" class="pa-10 text-center elevment-card elevation-0">
          <v-progress-circular indeterminate color="#00E5FF" size="48" />
          <p class="mt-4 text--secondary">Carregando seus PDIs...</p>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" max-width="800px" persistent>
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4 d-flex justify-space-between align-center">
          <h3 class="text-h5 font-weight-bold text--primary">{{ editMode ? 'Editar PDI' : 'Novo PDI' }}</h3>
          <v-btn icon color="grey" @click="closeDialog" small><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider class="theme-divider" />

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="validForm">
            <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Título do PDI *</div>
            <v-text-field v-model="editedPdi.title" placeholder="Ex: Desenvolver habilidades de liderança" :rules="[v => !!v || 'Título é obrigatório']" outlined dense class="elevment-input mb-4" color="#00E5FF" hide-details="auto" />

            <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Descrição *</div>
            <v-textarea v-model="editedPdi.description" placeholder="Descreva o que você pretende desenvolver" :rules="[v => !!v || 'Descrição é obrigatória']" outlined rows="3" class="elevment-input mb-4" color="#00E5FF" hide-details="auto" />

            <v-row>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Data Final *</div>
                <v-text-field v-model="editedPdi.deadline" type="date" :min="minDate" :rules="[v => !!v || 'Data final é obrigatória']" outlined dense class="elevment-input mb-4" color="#00E5FF" hide-details="auto" prepend-inner-icon="mdi-calendar" />
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Categoria *</div>
                <v-select v-model="editedPdi.category" :items="categoryOptions" placeholder="Selecione a categoria" :rules="[v => !!v || 'Categoria é obrigatória']" outlined dense class="elevment-input mb-4" color="#00E5FF" item-color="cyan" hide-details="auto" prepend-inner-icon="mdi-tag" />
              </v-col>
            </v-row>

            <v-divider class="theme-divider my-4" />

            <div class="d-flex justify-space-between align-center mb-3">
              <span class="text-subtitle-2 font-weight-bold text--primary">Metas *</span>
              <v-btn small color="#00E5FF" outlined class="font-weight-bold action-btn-outlined" :disabled="isViewOnlyMode" @click="addGoal">
                <v-icon small left>mdi-plus</v-icon> Adicionar Meta
              </v-btn>
            </div>

            <v-alert v-if="editedPdi.goals.length === 0" type="info" outlined dense class="elevment-alert mb-3 text-body-2">
              Adicione pelo menos uma meta ao seu PDI
            </v-alert>

            <v-list dense class="transparent-bg">
              <v-list-item v-for="(goal, index) in editedPdi.goals" :key="index" class="px-0 mb-2">
                <v-list-item-content>
                  <v-text-field v-model="goal.title" dense outlined hide-details placeholder="Ex: Concluir curso de gestão" :rules="[v => !!v || 'Obrigatório']" class="elevment-input" color="#00E5FF" />
                </v-list-item-content>
                <v-list-item-action class="my-0 ml-2">
                  <v-btn icon small color="error" :disabled="isViewOnlyMode" @click="removeGoal(index)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <div v-if="!editMode" class="mt-6 pa-4 elevment-box">
              <div class="text-subtitle-2 font-weight-bold mb-3 text--primary">
                <v-icon small left color="#00E5FF">mdi-send</v-icon> O que deseja fazer com este PDI?
              </div>
              <v-radio-group v-model="submitAction" hide-details class="mt-0 custom-radio">
                <v-radio value="DRAFT" color="#00E5FF">
                  <template #label>
                    <div>
                      <div class="font-weight-medium text--primary">Salvar como Rascunho</div>
                      <div class="text-caption text--secondary">Você poderá editar e enviar para aprovação depois</div>
                    </div>
                  </template>
                </v-radio>
                <v-radio value="PENDING_APPROVAL" color="#00E5FF" class="mt-3">
                  <template #label>
                    <div>
                      <div class="font-weight-medium text--primary">Enviar para Aprovação</div>
                      <div class="text-caption text--secondary">O PDI será enviado diretamente para análise do gestor</div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </div>
          </v-form>
        </v-card-text>

        <v-divider class="theme-divider" />
        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn text height="40" class="px-5 font-weight-bold text--secondary" style="border-radius: 8px;" @click="closeDialog">Cancelar</v-btn>
          <v-btn :color="editMode ? 'success' : (submitAction === 'PENDING_APPROVAL' ? '#00E5FF' : 'grey')" height="40" depressed class="px-6 font-weight-bold" :class="submitAction === 'PENDING_APPROVAL' || !editMode ? 'black--text' : 'white--text'" style="border-radius: 8px;" :loading="saving" :disabled="isViewOnlyMode || editedPdi.goals.length === 0" @click="savePdi">
            <v-icon left :color="submitAction === 'PENDING_APPROVAL' || !editMode ? 'black' : 'white'">{{ editMode ? 'mdi-content-save' : (submitAction === 'PENDING_APPROVAL' ? 'mdi-send' : 'mdi-content-save') }}</v-icon>
            {{ editMode ? 'Atualizar' : (submitAction === 'PENDING_APPROVAL' ? 'Enviar para Aprovação' : 'Salvar como Rascunho') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="goalsDialog" max-width="800px" scrollable>
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4 d-flex justify-space-between align-center">
          <h3 class="text-h5 font-weight-bold text--primary">Marcar Metas como Concluídas</h3>
          <v-btn icon color="grey" @click="closeGoalsDialog" small><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider class="theme-divider" />

        <v-card-text class="pa-6">
          <v-alert type="info" outlined dense class="elevment-alert mb-4 text-body-2">
            <strong>Fluxo:</strong> Expanda a meta, preencha a evidência de conclusão e depois marque como concluída.
          </v-alert>

          <v-expansion-panels v-model="expandedGoalPanels" multiple flat class="elevment-panels">
            <v-expansion-panel v-for="(goal, index) in selectedPdiGoals" :key="index" class="mb-2 elevment-panel">
              <v-expansion-panel-header>
                <div class="d-flex align-center">
                  <v-checkbox :input-value="goal.status === 'DONE'" @click.prevent.stop="isViewOnlyMode ? null : toggleGoalStatus(goal, index)" color="#00E5FF" :disabled="isViewOnlyMode" class="mt-0 pt-0" hide-details />
                  <v-chip v-if="!isViewOnlyMode && goal.needsEvidence" x-small outlined color="error" class="mt-1 ml-2 font-weight-bold">
                    <v-icon x-small left>mdi-alert</v-icon> Faltam evidências
                  </v-chip>
                  <div class="ml-3">
                    <div :class="goal.status === 'DONE' ? 'text-decoration-line-through text--secondary' : 'font-weight-medium text--primary'">
                      {{ goal.title }}
                    </div>
                    <v-chip v-if="goal.status === 'DONE'" outlined x-small class="theme-badge success--text mt-1">
                      <v-icon x-small left>mdi-check</v-icon> Concluída
                    </v-chip>
                  </div>
                </div>
              </v-expansion-panel-header>
              
              <v-expansion-panel-content>
                <v-divider class="theme-divider mb-4 mt-2" />
                
                <div v-if="goal.description" class="mb-4">
                  <div class="text-caption text--secondary mb-1">Descrição da Meta:</div>
                  <p class="text-body-2 text--primary mb-0">{{ goal.description }}</p>
                </div>

                <div class="text-caption font-weight-bold text--primary mb-2">Evidência de Conclusão *</div>
                <v-textarea :ref="'evidence-' + index" v-model="goal.comments" placeholder="Descreva resultados alcançados..." outlined rows="3" class="elevment-input mb-3" color="#00E5FF"
                  :error="goal.needsEvidence || (goal.status === 'DONE' && (!goal.comments || goal.comments.trim().length < 10))"
                  :error-messages="goal.needsEvidence || (goal.status === 'DONE' && (!goal.comments || goal.comments.trim().length < 10)) ? 'Mínimo 10 caracteres' : ''"
                  @input="goal.needsEvidence = false"
                />

                <div class="elevment-box pa-4">
                  <div class="text-subtitle-2 text--primary mb-2">
                    <v-icon small left color="primary">mdi-paperclip</v-icon> Anexar Comprovante (opcional)
                  </div>
                  <v-btn outlined color="primary" class="font-weight-bold action-btn-outlined" :disabled="isViewOnlyMode" @click="selectFileForGoal(goal)" block small>
                    <v-icon left small>mdi-upload</v-icon> {{ goal.evidenceUrl ? 'Substituir Arquivo' : 'Selecionar Arquivo' }}
                  </v-btn>
                  <div class="text-caption text--secondary mt-2 text-center">Formatos: Imagens, PDF, DOC. Máx: 5MB</div>
                  
                  <div v-if="goal.evidenceUrl" class="mt-3 text-center">
                    <v-chip small outlined color="primary" class="theme-badge font-weight-bold" @click="viewEvidence(goal.evidenceUrl)" style="cursor: pointer;">
                      <v-icon small left>mdi-file-check</v-icon> Ver anexo salvo
                    </v-chip>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-divider class="theme-divider my-4" />

          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-2 text--secondary">Progresso Global:</span>
            <span class="text-h6 font-weight-bold" :class="getProgressColor(calculateGoalsProgress()) + '--text'">{{ calculateGoalsProgress() }}%</span>
          </div>
          <v-progress-linear :value="calculateGoalsProgress()" :color="getProgressColor(calculateGoalsProgress())" height="8" rounded background-color="rgba(128,128,128,0.1)" />
        </v-card-text>

        <v-divider class="theme-divider" />
        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn text height="40" class="px-5 font-weight-bold text--secondary" style="border-radius: 8px;" @click="closeGoalsDialog">Cancelar</v-btn>
          <v-btn color="#00E5FF" height="40" depressed class="px-6 font-weight-bold black--text" style="border-radius: 8px;" :loading="saving" :disabled="isViewOnlyMode || !validateGoalsEvidence()" @click="saveGoalsProgress">
            <v-icon left color="black">mdi-content-save</v-icon> Salvar Progresso
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4">
          <h3 class="text-h5 font-weight-bold error--text"><v-icon left color="error">mdi-alert</v-icon> Confirmar Exclusão</h3>
        </v-card-title>
        <v-card-text class="pa-6 pt-0">
          <p class="text-body-1 text--primary">Tem certeza que deseja excluir o PDI <strong>"{{ pdiToDelete?.title }}"</strong>?</p>
          <p class="text-body-2 text--secondary mb-0">Esta ação não pode ser desfeita.</p>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn text class="font-weight-bold text--secondary" @click="cancelDelete">Cancelar</v-btn>
          <v-btn color="error" depressed class="font-weight-bold" @click="confirmDelete">Excluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="lastGoalConfirmDialog" max-width="550px" persistent>
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4">
          <h3 class="text-h5 font-weight-bold warning--text"><v-icon left color="warning">mdi-alert-circle</v-icon> Atenção - Última Meta</h3>
        </v-card-title>
        <v-card-text class="pa-6 pt-0">
          <p class="text-body-1 text--primary font-weight-bold">Você está prestes a marcar a última meta deste PDI!</p>
          <p class="text-body-2 text--secondary mb-4">Ao confirmar, o seguinte acontecerá:</p>
          
          <v-list dense class="transparent-bg mb-4">
            <v-list-item class="px-0 min-h-auto mb-2"><v-icon small color="success" class="mr-3">mdi-check-circle</v-icon><div class="text-body-2 text--primary">Seu PDI ficará com <strong>100% de progresso</strong></div></v-list-item>
            <v-list-item class="px-0 min-h-auto mb-2"><v-icon small color="primary" class="mr-3">mdi-send</v-icon><div class="text-body-2 text--primary">Seu PDI será <strong>enviado para aprovação</strong> do gestor</div></v-list-item>
            <v-list-item class="px-0 min-h-auto mb-2"><v-icon small color="warning" class="mr-3">mdi-lock</v-icon><div class="text-body-2 text--primary">Ficará <strong>bloqueado para edição</strong> até a aprovação</div></v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn text class="font-weight-bold text--secondary" @click="cancelLastGoalMark">Cancelar</v-btn>
          <v-btn color="success" depressed class="font-weight-bold" @click="confirmLastGoalMark">Confirmar e Marcar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top elevation="0" rounded="lg">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false" color="white" class="font-weight-bold">Ok</v-btn>
      </template>
    </v-snackbar>

    <input ref="evidenceFileInput" type="file" accept="image/*,.pdf,.doc,.docx" style="display: none" @change="onEvidenceFileSelected">
  </v-container>
</template>

<script>
import { PdisService, FilesService, OneOnOnesService } from '@/services'

export default {
  name: 'PDIUser',

  data: () => ({
    loading: false,
    pdis: [],
    expandedPdis: [],
    uploading: false,
    currentUploadGoal: null,
    pdiFilter: 'all',
    
    dialog: false,
    editMode: false,
    validForm: false,
    saving: false,
    submitAction: 'DRAFT',
    
    goalsDialog: false,
    selectedPdi: null,
    selectedPdiGoals: [],
    expandedGoalPanels: [],
    
    deleteDialog: false,
    pdiToDelete: null,
    
    lastGoalConfirmDialog: false,
    pendingGoalsSave: false,
    
    editedPdi: {
      title: '', description: '', deadline: '', category: '', status: 'DRAFT', goals: [],
    },
    defaultPdi: {
      title: '', description: '', deadline: '', category: '', status: 'DRAFT', goals: [],
    },
    
    categoryOptions: [
      'Liderança', 'Tecnologia', 'Comunicação', 'Gestão de Projetos',
      'Desenvolvimento Pessoal', 'Habilidades Técnicas', 'Soft Skills', 'Processos', 'Inovação',
    ],
    
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),

  computed: {
    isViewOnlyMode() {
      try { return this.$store?.state?.viewOnlyMode === true } catch (error) { return false }
    },
    currentUser() {
      try {
        const userStr = localStorage.getItem('user')
        return userStr ? JSON.parse(userStr) : null
      } catch (error) { return null }
    },
    canSelfApprove() {
      if (!this.currentUser) return false
      return this.currentUser.role === 'MANAGER' && !this.currentUser.managerId
    },
    activePdisCount() {
      return this.pdis.filter(pdi => pdi.status === 'APPROVED' || pdi.status === 'IN_PROGRESS').length
    },
    totalGoalsCount() {
      return this.pdis
        .filter(pdi => pdi.status !== 'CANCELLED' && pdi.status !== 'REJECTED')
        .reduce((total, pdi) => total + (pdi.goals?.length || 0), 0)
    },
    completedGoalsCount() {
      return this.pdis
        .filter(pdi => pdi.status !== 'CANCELLED' && pdi.status !== 'REJECTED')
        .reduce((total, pdi) => {
          const completedInPdi = pdi.goals?.filter(g => g.status === 'DONE' || g.completed === true).length || 0
          return total + completedInPdi
        }, 0)
    },
    goalsCompletionPercentage() {
      if (this.totalGoalsCount === 0) return 0
      return Math.round((this.completedGoalsCount / this.totalGoalsCount) * 100)
    },
    filteredPdis() {
      if (this.pdiFilter === 'all') return this.pdis
      const statusMap = {
        'draft': 'DRAFT', 'pending_approval': 'PENDING_APPROVAL', 'approved': 'APPROVED',
        'in_progress': 'IN_PROGRESS', 'completed': 'COMPLETED', 'rejected': 'REJECTED',
      }
      return this.pdis.filter(pdi => pdi.status === statusMap[this.pdiFilter])
    },
    minDate() {
      const today = new Date()
      return today.toISOString().split('T')[0]
    },
  },

  mounted() {
    this.loadPdis()
  },

  methods: {
    async loadPdis() {
      this.loading = true
      try {
        const response = await PdisService.list()
        const currentUserId = this.currentUser?.id
        this.pdis = response.data.filter(pdi => pdi.userId === currentUserId)
      } catch (error) {
        this.showSnackbar('Erro ao carregar PDIs', 'error')
      } finally {
        this.loading = false
      }
    },

    calculatePdiProgress(pdi) {
      if (!pdi.goals || pdi.goals.length === 0) return 0
      const completed = pdi.goals.filter(g => g.status === 'DONE' || g.completed === true).length
      return Math.round((completed / pdi.goals.length) * 100)
    },

    toggleExpand(pdiId) {
      const index = this.expandedPdis.indexOf(pdiId)
      if (index > -1) this.expandedPdis.splice(index, 1)
      else this.expandedPdis.push(pdiId)
    },

    openCreateDialog() {
      this.editMode = false
      this.editedPdi = { ...this.defaultPdi, goals: [], category: '' }
      this.submitAction = 'DRAFT'
      this.dialog = true
    },

    editPdi(pdi) {
      if (pdi.status !== 'DRAFT' && pdi.status !== 'PENDING_APPROVAL') {
        this.showSnackbar('Apenas PDIs em rascunho ou em aprovação podem ser editados', 'warning')
        return
      }
      this.editMode = true
      this.editedPdi = {
        ...pdi,
        goals: pdi.goals ? pdi.goals.map(g => ({ ...g, status: g.status || 'PENDING' })) : [],
      }
      this.dialog = true
    },

    async deletePdi(pdi) {
      this.pdiToDelete = pdi
      this.deleteDialog = true
    },

    async confirmDelete() {
      if (!this.pdiToDelete) return
      try {
        this.loading = true
        this.deleteDialog = false
        await PdisService.delete(this.pdiToDelete.id)
        this.showSnackbar('PDI excluído com sucesso!', 'success')
        await this.loadPdis()
      } catch (error) {
        const errorMsg = error.response?.data?.message || 'Erro ao excluir PDI'
        this.showSnackbar(errorMsg, 'error')
      } finally {
        this.loading = false
        this.pdiToDelete = null
      }
    },

    cancelDelete() {
      this.deleteDialog = false
      this.pdiToDelete = null
    },

    confirmLastGoalMark() {
      this.lastGoalConfirmDialog = false
      this.pendingGoalsSave = true
      this.saveGoalsProgress()
    },

    cancelLastGoalMark() {
      this.lastGoalConfirmDialog = false
      this.pendingGoalsSave = false
    },

    closeDialog() {
      this.dialog = false
      this.$refs.form?.reset()
    },

    async savePdi() {
      if (!this.$refs.form.validate()) return
      if (this.editedPdi.goals.length === 0) {
        this.showSnackbar('Adicione pelo menos uma meta ao PDI', 'warning')
        return
      }
      if (this.editedPdi.goals.filter(g => !g.title || g.title.trim() === '').length > 0) {
        this.showSnackbar('Preencha o título de todas as metas', 'warning')
        return
      }

      if (!this.editMode) {
        this.editedPdi.goals = this.editedPdi.goals.map(goal => ({ ...goal, status: 'PENDING' }))
        this.editedPdi.progress = 0
        this.editedPdi.status = this.submitAction
      } else {
        this.editedPdi.progress = this.calculatePdiProgress(this.editedPdi)
      }

      this.saving = true
      try {
        if (this.editMode) {
          await PdisService.update(this.editedPdi.id, { ...this.editedPdi, userId: this.editedPdi.userId || this.currentUser?.id })
          this.showSnackbar('PDI atualizado com sucesso!', 'success')
        } else {
          await PdisService.create(this.editedPdi)
          this.showSnackbar(this.submitAction === 'PENDING_APPROVAL' ? 'PDI enviado para aprovação!' : 'PDI salvo como rascunho!', 'success')
        }
        this.closeDialog()
        this.loadPdis()
      } catch (error) {
        this.showSnackbar('Erro ao salvar PDI', 'error')
      } finally {
        this.saving = false
      }
    },

    async submitForApproval(pdi) {
      if (!pdi.goals || pdi.goals.length === 0) {
        this.showSnackbar('Adicione pelo menos uma meta', 'warning')
        return
      }
      try {
        await PdisService.updateStatus(pdi.id, 'PENDING_APPROVAL')
        this.showSnackbar('PDI enviado para aprovação!', 'success')
        this.loadPdis()
      } catch (error) {
        this.showSnackbar('Erro ao enviar PDI para aprovação', 'error')
      }
    },

    async startPdi(pdi) {
      try {
        await PdisService.updateStatus(pdi.id, 'IN_PROGRESS')
        this.showSnackbar('PDI iniciado!', 'success')
        this.loadPdis()
      } catch (error) {
        this.showSnackbar('Erro ao iniciar PDI', 'error')
      }
    },

    openGoalsDialog(pdi) {
      this.selectedPdi = { ...pdi }
      this.selectedPdiGoals = pdi.goals ? pdi.goals.map(g => ({
        ...g,
        status: g.status || (g.completed ? 'DONE' : 'PENDING'),
        comments: g.comments || '',
      })) : []
      this.goalsDialog = true
    },

    closeGoalsDialog() {
      this.goalsDialog = false
      this.selectedPdi = null
      this.selectedPdiGoals = []
      this.expandedGoalPanels = []
    },

    toggleGoalStatus(goal, index) {
      if (goal.status !== 'DONE' && (!goal.comments || goal.comments.trim().length < 10)) {
        this.$set(goal, 'needsEvidence', true)
        if (!this.expandedGoalPanels.includes(index)) this.expandedGoalPanels.push(index)
        this.$nextTick(() => {
          const field = this.$refs['evidence-' + index]
          if (field && field[0]) field[0].focus()
        })
        return
      }
      if (goal.needsEvidence) this.$set(goal, 'needsEvidence', false)
      goal.status = goal.status === 'DONE' ? 'PENDING' : 'DONE'
    },

    calculateGoalsProgress() {
      if (!this.selectedPdiGoals || this.selectedPdiGoals.length === 0) return 0
      const completed = this.selectedPdiGoals.filter(g => g.status === 'DONE').length
      return Math.round((completed / this.selectedPdiGoals.length) * 100)
    },

    async saveGoalsProgress() {
      if (!this.validateGoalsEvidence()) {
        this.showSnackbar('Preencha a evidência para todas as metas concluídas', 'warning')
        return
      }

      const progress = this.calculateGoalsProgress()
      const isMarkingLastGoal = progress === 100 && (this.selectedPdi.progress || 0) < 100

      if (isMarkingLastGoal && !this.pendingGoalsSave && !this.canSelfApprove) {
        this.lastGoalConfirmDialog = true
        return
      }

      this.saving = true
      try {
        const updatedPdi = {
          ...this.selectedPdi,
          goals: this.selectedPdiGoals.map(g => ({ ...g })),
          progress,
          status: 'IN_PROGRESS'
        }
        await PdisService.update(updatedPdi.id, updatedPdi)
        
        if (progress === 100) {
          this.showSnackbar(this.canSelfApprove ? 'Todas as metas concluídas!' : 'PDI enviado para aprovação final.', 'success')
        } else {
          this.showSnackbar(`Progresso: ${progress}%`, 'success')
        }
        
        this.pendingGoalsSave = false
        this.closeGoalsDialog()
        this.loadPdis()
      } catch (error) {
        const errStr = error.response?.data || ''
        if (typeof errStr === 'string' && errStr.includes('10 caracteres')) {
           this.showSnackbar('Evidência insuficiente (mín. 10 char)', 'warning')
        } else {
           this.showSnackbar('Erro ao atualizar progresso', 'error')
        }
      } finally {
        this.saving = false
      }
    },

    validateGoalsEvidence() {
      return this.selectedPdiGoals.filter(g => g.status === 'DONE').every(g => g.comments && g.comments.trim().length >= 10)
    },

    selectFileForGoal(goal) {
      this.currentUploadGoal = goal
      this.$refs.evidenceFileInput.click()
    },

    async onEvidenceFileSelected(event) {
      const file = event.target.files[0]
      if (!file || !this.currentUploadGoal) return

      if (file.size > 5 * 1024 * 1024) {
        this.showSnackbar('Máximo de 5MB permitidos.', 'warning')
        event.target.value = ''
        return
      }

      this.uploading = true
      this.showSnackbar('Fazendo upload...', 'info')

      try {
        const response = await FilesService.upload(file, { context: 'pdi-evidence' })
        this.currentUploadGoal.evidenceUrl = response.data.url
        this.currentUploadGoal.evidenceFilename = file.name
        this.showSnackbar('Anexado com sucesso!', 'success')
      } catch (error) {
        this.showSnackbar('Erro ao enviar arquivo', 'error')
      } finally {
        this.uploading = false
        this.currentUploadGoal = null
        event.target.value = ''
      }
    },

    viewEvidence(url) {
      window.open(url, '_blank')
    },

    async completePdi(pdi) {
      try {
        await PdisService.updateStatus(pdi.id, 'COMPLETED')
        this.showSnackbar('Parabéns! PDI concluído com sucesso!', 'success')
        this.loadPdis()
      } catch (error) {
        this.showSnackbar('Erro ao concluir PDI', 'error')
      }
    },

    addGoal() {
      this.editedPdi.goals.push({ title: '', status: 'PENDING', description: '' })
    },
    removeGoal(index) {
      this.editedPdi.goals.splice(index, 1)
    },

    getStatusColor(status) {
      const colors = {
        DRAFT: 'grey', PENDING_APPROVAL: 'orange', APPROVED: 'green',
        REJECTED: 'red', IN_PROGRESS: 'blue', COMPLETED: 'success', CANCELLED: 'grey',
      }
      return colors[status] || 'grey'
    },

    getStatusIcon(status) {
      const icons = {
        DRAFT: 'mdi-file-document-edit', PENDING_APPROVAL: 'mdi-clock-alert', APPROVED: 'mdi-check-circle',
        REJECTED: 'mdi-close-circle', IN_PROGRESS: 'mdi-progress-clock', COMPLETED: 'mdi-check-all', CANCELLED: 'mdi-cancel',
      }
      return icons[status] || 'mdi-file'
    },

    getStatusLabel(status) {
      const labels = {
        DRAFT: 'Rascunho', PENDING_APPROVAL: 'Em Aprovação', APPROVED: 'Aprovado',
        REJECTED: 'Rejeitado', IN_PROGRESS: 'Em Progresso', COMPLETED: 'Concluído', CANCELLED: 'Cancelado',
      }
      return labels[status] || status
    },

    getStatusChipClass(status) {
      const classes = {
        DRAFT: 'grey--text', PENDING_APPROVAL: 'orange--text', APPROVED: 'success--text',
        REJECTED: 'error--text', IN_PROGRESS: 'blue--text', COMPLETED: 'success--text', CANCELLED: 'grey--text',
      }
      return classes[status] || 'grey--text'
    },

    getProgressColor(progress) {
      if (progress >= 100) return 'success'
      if (progress >= 50) return 'blue'
      if (progress >= 1) return 'orange'
      return 'grey'
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('pt-BR')
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
  },
}
</script>

<style scoped>
/* Fundo Global Premium */
.dashboard-container {
  background-color: var(--bg-main, #0D0D0D) !important;
  max-width: 1440px;
  margin: 0 auto;
}

/* Base Cards */
.elevment-card {
  border-radius: 12px !important;
  border: 1px solid var(--border-color, rgba(128,128,128,0.15)) !important;
  background-color: var(--bg-card, #1A1A1A) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.meeting-list-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
}

/* Tags Delineadas / Pílulas Globais */
::v-deep .v-chip {
  border-radius: 9999px !important;
  background: transparent !important;
  border: 1px solid currentColor !important;
  text-transform: uppercase;
  font-weight: 700 !important;
  font-size: 10px !important;
}

/* Filtros */
.theme-chip-filter {
  border-color: var(--border-color, rgba(128,128,128,0.2)) !important;
  color: var(--text-secondary, #86868B) !important;
}
.theme-chip-filter.active {
  border-color: #00E5FF !important;
  color: #00E5FF !important;
  background-color: rgba(0, 229, 255, 0.05) !important;
}

/* Tópicos Normais (Não uppercase) */
::v-deep .theme-chip-topic {
  text-transform: none !important;
  font-weight: 500 !important;
  color: var(--text-secondary, #AEAEB2) !important;
  border-color: var(--border-color, rgba(128,128,128,0.2)) !important;
}

/* Componentes Base - Inputs, Dialogs e Divider */
.theme-divider {
  border-color: var(--border-color, rgba(128,128,128,0.15)) !important;
}

.elevment-dialog-card {
  border-radius: 12px !important;
  border: 1px solid var(--border-color, rgba(128,128,128,0.15)) !important;
  background-color: var(--bg-card, #1A1A1A) !important;
}

.elevment-box {
  background-color: var(--bg-secondary, rgba(128,128,128,0.05));
  border-radius: 8px;
  border: 1px solid var(--border-color, rgba(128,128,128,0.1));
}

.elevment-alert {
  border-radius: 8px !important;
  background-color: transparent !important;
}

/* Expansion Panels */
.transparent-bg { background-color: transparent !important; }
.min-h-auto { min-height: auto !important; }

::v-deep .elevment-panels .v-expansion-panel {
  background-color: transparent !important;
  border: 1px solid var(--border-color, rgba(128,128,128,0.15)) !important;
  border-radius: 8px !important;
}
::v-deep .elevment-panels .v-expansion-panel::before {
  box-shadow: none !important;
}

/* Radio Customizado (SaaS Style) */
::v-deep .custom-radio .v-radio {
  padding: 12px;
  border: 1px solid var(--border-color, rgba(128,128,128,0.15));
  border-radius: 8px;
  margin-bottom: 8px !important;
  transition: 0.2s ease;
}
::v-deep .custom-radio .v-radio.v-item--active {
  border-color: #00E5FF;
  background-color: rgba(0, 229, 255, 0.05);
}

.action-btn-outlined { letter-spacing: 0 !important; }
.expanded-area { border-radius: 0 0 12px 12px; }

/* Custom Scrollbar */
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-thumb { background: rgba(128, 128, 128, 0.3); border-radius: 3px; }
::-webkit-scrollbar-track { background: transparent; }
</style>